import { appState } from "./state";
import { notifyStateChange } from "./stateBus";
import { apiFetch } from "./api";
import { requestLock } from "./lockBus";
import { showToast } from "./toast";
import { loadGalleryData } from "./galleryApi";
import type { AlbumActivityEntry } from "./types";

// Framework-agnostic album data operations - zero dom.ts dependency, safe
// for direct import from React. As of Phase 10 (nav/routing consolidation),
// albums.ts and albumsBridge.ts are gone entirely - navigation is
// react-router routes now, and deleteAlbumAndRefreshGallery (previously
// reached through albumsBridge.ts since it needed the gallery-grid
// refresh that gallery.ts alone used to own) has no dom.ts dependency
// either, so it lives here directly.

export async function loadAlbums(): Promise<void> {
  try {
    const res = await apiFetch("/api/albums");
    if (res.status === 401) {
      requestLock("unauthorized");
      return;
    }
    if (!res.ok) throw new Error("Failed to load albums");

    const data = await res.json();
    appState.albums = Array.isArray(data.albums) ? data.albums : [];

    appState.albums.sort((a, b) => {
      const ta = Date.parse(a?.updatedAt || a?.createdAt || "") || 0;
      const tb = Date.parse(b?.updatedAt || b?.createdAt || "") || 0;
      return tb - ta;
    });

    notifyStateChange();
  } catch (err) {
    console.error("Failed to load albums:", err);
  }
}

export async function createAlbum(name: string, description: string): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const res = await apiFetch("/api/albums", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, description }),
    });

    if (res.status === 401) {
      // requestLock("unauthorized") is enough on its own - AuthGate.tsx's
      // lock handler already calls /api/logout itself on every lock, so
      // doing it here too just fired the same request twice.
      requestLock("unauthorized");
      return { ok: false, error: "Unauthorized" };
    }

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: data?.error || "Failed to create album" };
    }

    await loadAlbums();
    return { ok: true };
  } catch {
    return { ok: false, error: "Failed to create album. Try again." };
  }
}

export async function renameAlbumApi(albumId: string, newName: string): Promise<boolean> {
  try {
    const res = await apiFetch(`/api/albums/${albumId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName }),
    });

    if (res.status === 401) {
      // See createAlbum's 401 handler above - AuthGate.tsx's lock handler
      // already calls /api/logout on every lock, so requestLock alone is
      // enough here too.
      requestLock("unauthorized");
      return false;
    }

    if (res.ok) {
      await loadAlbums();
      showToast("Album renamed", "success");
      return true;
    }
    showToast("Failed to rename album.", "error");
    return false;
  } catch {
    showToast("Failed to rename album.", "error");
    return false;
  }
}

export async function deleteAlbumApi(albumId: string): Promise<boolean> {
  try {
    const res = await apiFetch(`/api/albums/${albumId}`, { method: "DELETE" });

    if (res.status === 401) {
      // See createAlbum's 401 handler above - AuthGate.tsx's lock handler
      // already calls /api/logout on every lock, so requestLock alone is
      // enough here too.
      requestLock("unauthorized");
      return false;
    }

    if (res.ok) {
      if (appState.currentAlbum === albumId) appState.currentAlbum = "all";
      await loadAlbums();
      showToast("Album deleted", "success");
      return true;
    }
    showToast("Failed to delete album.", "error");
    return false;
  } catch {
    showToast("Failed to delete album.", "error");
    return false;
  }
}

export async function deleteAlbumAndRefreshGallery(albumId: string): Promise<boolean> {
  const ok = await deleteAlbumApi(albumId);
  if (ok) await loadGalleryData();
  return ok;
}

// Returns null (not []) on failure, distinct from "no activity yet" - lets
// AlbumsListScreen.tsx keep the rest of the Albums page fully usable and
// just hide the Recent Activity section rather than showing a false empty
// state when the request itself failed.
export async function fetchAlbumActivity(limit = 10): Promise<AlbumActivityEntry[] | null> {
  try {
    const res = await apiFetch(`/api/albums/activity?limit=${limit}`);
    if (res.status === 401) {
      requestLock("unauthorized");
      return null;
    }
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data.activity) ? data.activity : [];
  } catch {
    return null;
  }
}

export async function addAlbumTagApi(albumId: string, tag: string): Promise<{ ok: true; tags: string[] } | { ok: false; error: string }> {
  try {
    const res = await apiFetch(`/api/albums/${albumId}/tags`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tag }),
    });
    if (res.status === 401) {
      requestLock("unauthorized");
      return { ok: false, error: "Unauthorized" };
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: data?.message || data?.error || "Failed to add tag" };
    await loadAlbums();
    return { ok: true, tags: data.tags || [] };
  } catch {
    return { ok: false, error: "Failed to add tag. Try again." };
  }
}

export async function removeAlbumTagApi(albumId: string, tag: string): Promise<{ ok: true; tags: string[] } | { ok: false; error: string }> {
  try {
    const res = await apiFetch(`/api/albums/${albumId}/tags/${encodeURIComponent(tag)}`, { method: "DELETE" });
    if (res.status === 401) {
      requestLock("unauthorized");
      return { ok: false, error: "Unauthorized" };
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: data?.message || data?.error || "Failed to remove tag" };
    await loadAlbums();
    return { ok: true, tags: data.tags || [] };
  } catch {
    return { ok: false, error: "Failed to remove tag. Try again." };
  }
}
